import clsx from 'clsx'
import { type ReactElement, type SyntheticEvent, useRef, useState } from 'react'

import { ControlsLocation } from '@constants/enums/video'

import { Button } from '@components/ui-core/button/button'
import { Video, type VideoProps } from '@components/ui-core/video/video'

import { IconPause } from './icon-pause'
import { IconPlay } from './icon-play'

import styles from './animation-video.module.scss'

interface AnimationVideoProps {
  controlsLocation?: ControlsLocation
  videoOptions: VideoProps
  className?: string
  isRounded?: boolean
}

const controlsLocationStyles = {
  [ControlsLocation.OutsideBottomLeft]: styles.outsideBottomLeft,
  [ControlsLocation.InsideBottomLeft]: styles.insideBottomLeft,
  [ControlsLocation.InsideTopRight]: styles.insideTopRight,
  [ControlsLocation.InsideBottomRight]: styles.insideBottomRight,
}

export function AnimationVideo(props: AnimationVideoProps): ReactElement {
  const {
    controlsLocation = ControlsLocation.OutsideBottomLeft,
    videoOptions,
    className,
    isRounded,
  } = props

  const videoRef = useRef<HTMLVideoElement>(null)
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(videoOptions.autoPlay)

  const playAnimation = (play: boolean) => {
    if (!videoRef?.current) {
      return
    }
    setIsAnimationPlaying(play)
    play ? videoRef?.current?.play() : videoRef?.current?.pause()
  }

  const onPlayPauseClick = () => playAnimation(!isAnimationPlaying)

  const onVideoEnded = (loop: boolean) => playAnimation(loop)

  const [showControls, setShowControls] = useState(false)
  const onLoadedMetadata = (e: SyntheticEvent<HTMLVideoElement, Event>) => {
    const target = e.target as HTMLVideoElement
    // If video is longer than 5 seconds, show the play pause button
    if (target?.duration > 5) {
      setShowControls(true)
    }
  }

  return (
    <div className={clsx(styles.animationVideo, className)}>
      <Video
        ref={videoRef}
        muted
        playsInline
        controls={false}
        onVideoEnded={onVideoEnded}
        onLoadedMetadata={onLoadedMetadata}
        className={isRounded ? styles.isRounded : undefined}
        {...videoOptions}
      />
      {showControls && (
        <div className={clsx(styles.controls, controlsLocationStyles[controlsLocation])}>
          <Button
            category="unstyled"
            type="button"
            onClick={onPlayPauseClick}
            aria-label={isAnimationPlaying ? 'Pause animation' : 'Play animation'}
          >
            {isAnimationPlaying ? <IconPause /> : <IconPlay />}
          </Button>
        </div>
      )}
    </div>
  )
}
